import React from "react";
import { useQueryClient } from "@tanstack/react-query";
import Loader from "@components/Loader";
import {
  useOrderTypeDescriptionsQuery,
  useCreateOrderTypeDescriptionMutation,
  useSaveOrderTypeDescriptionMutation,
  useDeleteOrderTypeDescriptionMutation
} from "@queries/order_type_description";
import ImageUploader from "@components/upload/ImageUploader";
import config from "@config/config";
import "@styles/opencart-admin.css";

const ORDER_TYPES = [
  "car_cleaning",
  "normal",
  "skip",
  "express",
  "complementary",
  "disinfection",
  "furniture",
  "housekeeping",
  "carpets",
  "chemicals"
];

const defaultForm = {
  order_type: "normal",
  language_id: 1,
  title: "",
  description: "",
  image: ""
};

const imageBaseUrl = (config.imageBaseUrl || "").replace(/\/+$/, "") + "/";

export default function OrderTypeDescriptionPage() {
  const [filters, setFilters] = React.useState({
    title: "",
    order_type: "",
    language_id: ""
  });
  const [page, setPage] = React.useState(1);
  const pageSize = 20;

  const [editingId, setEditingId] = React.useState(null);
  const [form, setForm] = React.useState(defaultForm);

  const queryClient = useQueryClient();

  const {
    data,
    isLoading,
    isFetching,
    isError,
    error
  } = useOrderTypeDescriptionsQuery(
    {
      title: filters.title || undefined,
      order_type: filters.order_type || undefined,
      language_id: filters.language_id || undefined
    },
    page,
    pageSize
  );

  const createMutation = useCreateOrderTypeDescriptionMutation();
  const saveMutation = useSaveOrderTypeDescriptionMutation();
  const deleteMutation = useDeleteOrderTypeDescriptionMutation();

  const rows = data?.data || [];
  const total = data?.meta?.total ?? rows.length;
  const pages = Math.max(1, Math.ceil(total / pageSize));

  function resetForm() {
    setEditingId(null);
    setForm(defaultForm);
  }

  function openEdit(row) {
    setEditingId(row.id);
    setForm({
      order_type: row.order_type || "normal",
      language_id: row.language_id || 1,
      title: row.title || "",
      description: row.description || "",
      image: row.image || ""
    });
  }

  async function handleSave(e) {
    e.preventDefault();
    const payload = {
      ...form,
      language_id: Number(form.language_id) || 1
    };

    if (editingId) {
      await saveMutation.mutateAsync({ id: editingId, payload });
    } else {
      await createMutation.mutateAsync(payload);
    }

    resetForm();
    queryClient.invalidateQueries({
      queryKey: ["orders", "order-type-description"]
    });
  }

  async function handleDelete(id) {
    if (!window.confirm("Delete this order type description?")) return;
    await deleteMutation.mutateAsync(id);
    queryClient.invalidateQueries({
      queryKey: ["orders", "order-type-description"]
    });
  }

  function handleFilterChange(field, value) {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPage(1);
  }

  return (
    <div className="oc-content">
      {(isLoading || isFetching) && <Loader />}

      <div className="oc-header">
        <h1>Order Type</h1>
        <nav className="oc-bc">
          <span>Home</span>
          <span className="sep">›</span>
          <span>Order Type</span>
        </nav>
      </div>

      <div className="oc-card">
        <div className="oc-toolbar" style={{ marginBottom: 12 }}>
          <div>
            <strong>add description for order type</strong>
          </div>
          <div>
            <button className="oc-btn ghost" onClick={resetForm}>
              New
            </button>
          </div>
        </div>

        <form onSubmit={handleSave}>
          <div className="form-row">
            <div className="form-group">
              <label>Order Type</label>
              <select
                className="oc-input"
                value={form.order_type}
                onChange={(e) =>
                  setForm((f) => ({ ...f, order_type: e.target.value }))
                }
                required
              >
                {ORDER_TYPES.map((ot) => (
                  <option key={ot} value={ot}>
                    {ot}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Language ID</label>
              <input
                type="number"
                className="oc-input"
                value={form.language_id}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    language_id: e.target.value
                  }))
                }
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>title</label>
              <input
                className="oc-input"
                value={form.title}
                onChange={(e) =>
                  setForm((f) => ({ ...f, title: e.target.value }))
                }
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>description</label>
              <textarea
                className="oc-textarea"
                value={form.description}
                onChange={(e) =>
                  setForm((f) => ({ ...f, description: e.target.value }))
                }
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>image</label>
              <div className="oc-image-input-row">
                <input
                  className="oc-input"
                  value={form.image}
                  placeholder="image path"
                  onChange={(e) =>
                    setForm((f) => ({ ...f, image: e.target.value }))
                  }
                />
                <ImageUploader
                  value={form.image}
                  onChange={(newPath) => {
                    if (newPath) {
                      setForm((f) => ({ ...f, image: newPath }));
                    }
                  }}
                />
              </div>
              {form.image && (
                <div style={{ marginTop: 8 }}>
                  <img
                    src={imageBaseUrl + form.image}
                    alt=""
                    className="oc-thumb"
                  />
                </div>
              )}
            </div>
          </div>

          <div
            style={{
              marginTop: 12,
              display: "flex",
              justifyContent: "flex-end",
              gap: 8
            }}
          >
            <button
              type="button"
              className="oc-btn ghost"
              onClick={resetForm}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="oc-btn primary"
              disabled={
                createMutation.isLoading || saveMutation.isLoading
              }
            >
              Save
            </button>
          </div>
        </form>
      </div>

      <div
        className="oc-card"
        style={{ marginTop: 16 }}
      >
        <div className="oc-toolbar" style={{ marginBottom: 12 }}>
          <div>
            <strong>Order Type</strong>
          </div>
        </div>

        {isError && (
          <div style={{ color: "#c00", marginBottom: 8 }}>
            Error: {error?.message || "Unknown error"}
          </div>
        )}

        <div>
          <div>
            <table className="oc-table">
              <thead>
                <tr>
                  <th>title</th>
                  <th>Order Type</th>
                  <th>image</th>
                  <th style={{ width: 140 }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={4} style={{ textAlign: "center", padding: 24 }}>
                      No items
                    </td>
                  </tr>
                )}
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td>{row.title}</td>
                    <td>{row.order_type}</td>
                    <td>
                      {row.image && (
                        <img
                          src={imageBaseUrl + row.image}
                          alt=""
                          className="oc-thumb"
                        />
                      )}
                    </td>
                    <td>
                      <div style={{ display: "flex", gap: 8 }}>
                        <button
                          className="oc-btn blue"
                          onClick={() => openEdit(row)}
                        >
                          edit
                        </button>
                        <button
                          className="oc-btn danger"
                          onClick={() => handleDelete(row.id)}
                        >
                          delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="oc-card" style={{ display: "None" }}>
            <div className="title">Filter</div>
            <div className="form-group">
              <label>title</label>
              <input
                className="oc-input"
                value={filters.title}
                onChange={(e) =>
                  handleFilterChange("title", e.target.value)
                }
              />
            </div>
            <div className="form-group">
              <label>Order Type</label>
              <select
                className="oc-input"
                value={filters.order_type}
                onChange={(e) =>
                  handleFilterChange("order_type", e.target.value)
                }
              >
                <option value="">All</option>
                {ORDER_TYPES.map((ot) => (
                  <option key={ot} value={ot}>
                    {ot}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Language ID</label>
              <input
                className="oc-input"
                value={filters.language_id}
                onChange={(e) =>
                  handleFilterChange("language_id", e.target.value)
                }
              />
            </div>
          </div>
        </div>
      </div>

      <div
        className="oc-card"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginTop: 16
        }}
      >
        <div>
          Showing page <strong>{page}</strong> of <strong>{pages}</strong> (
          {total} total)
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            className="oc-btn ghost"
            disabled={page <= 1}
            onClick={() => setPage(1)}
          >
            « First
          </button>
          <button
            className="oc-btn ghost"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            ‹ Prev
          </button>
          <button
            className="oc-btn ghost"
            disabled={page >= pages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next ›
          </button>
          <button
            className="oc-btn ghost"
            disabled={page >= pages}
            onClick={() => setPage(pages)}
          >
            Last »
          </button>
        </div>
      </div>
    </div>
  );
}

